import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom-v5-compat'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useVerifyOTP } from '@/app/module/auth/hooks/use-verify-otp'

import { selectOTPToken } from '../store/session.slice'

type RecoveryCodeFormValues = {
  recoveryCode: string
}

const VerifyRecoveryCodeForm: React.FC = () => {
  const [verifyRecoveryCode, { isLoading }] = useVerifyOTP()

  const token = useSelector(selectOTPToken)

  const handleSubmit = (data: RecoveryCodeFormValues) => verifyRecoveryCode({ recovery: data.recoveryCode }, token)

  return (
    <StyledCard>
      <CardContent>
        <IconContainer>
          <StyledIcon>account_circle</StyledIcon>
        </IconContainer>
        <Typography color="textSecondary" variant="caption" sx={{ marginBottom: 2, display: 'block' }}>
          Please enter your recovery code
        </Typography>

        <Form
          defaultValues={defaultValues}
          fields={fields}
          schema={schema}
          shouldReset={false}
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <CardActions
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: '1rem',
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <Link to="/2fa">
                <Button size="small" color="primary">
                  Verify using a two-factor token instead
                </Button>
              </Link>
              <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
                Verify
              </SubmitButton>
            </CardActions>
          )}
        />

        <Typography color="textSecondary" variant="caption" sx={{ marginTop: 2, display: 'block' }}>
          You can enter one of your recovery codes in case you lost access to your mobile device.
        </Typography>
      </CardContent>
    </StyledCard>
  )
}

const schema = z.object({
  recoveryCode: z.string({ message: 'Recovery code is required' }).min(1, 'Recovery code is required'),
})

const defaultValues = {
  recoveryCode: '',
}

const fields = {
  recoveryCode: {
    fieldProps: {
      label: 'Code',
      name: 'recoveryCode',
      type: 'text',
      required: true,
      autoFocus: true,
    },
    type: 'text',
  },
} as const

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
  overflow: 'visible',
})

const IconContainer = styled(CardContent)({
  textAlign: 'center',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

export default VerifyRecoveryCodeForm
